/**
 * KPI distribution — one total and how it splits into states: the big number
 * with its label, a stacked bar, and a legend of dot + count + label.
 *
 * Use it instead of a KpiRow when the counters are shares of one whole (e.g.
 * CRA states per instance). Only items with `inBar` (the mutually exclusive
 * states) form the bar; overlapping counts such as „Frist überfällig“ appear
 * in the legend only. The number stays Off-Black, the dot carries the colour.
 *
 * With `onSelect` the legend entries are filter toggles, like clickable KPI
 * tiles: the selected one is outlined in orange (active state), a second click
 * hands the same key back so the page can clear the filter.
 */

import type { ReactNode } from 'react';

export type DistributionTone = 'good' | 'warn' | 'alert' | 'critical' | 'idle';

export interface DistributionItem {
  key: string;
  label: string;
  value: number;
  tone: DistributionTone;
  /** Part of the stacked bar (mutually exclusive states only). */
  inBar?: boolean;
  /** Native tooltip explaining the count. */
  title?: string;
}

export interface KpiDistributionProps {
  total: number;
  /** German, sentence case, e.g. "Instanzen geprüft". */
  totalLabel: string;
  items: readonly DistributionItem[];
  /** Accessible name of the block. */
  label?: string;
  /** Key of the legend entry whose filter is active. */
  selectedKey?: string | null;
  /** Makes the legend entries filter toggles. */
  onSelect?: (key: string) => void;
  /** id of the region the toggles filter. */
  controls?: string;
}

export function KpiDistribution({
  total, totalLabel, items, label = 'Kennzahlen', selectedKey = null, onSelect, controls,
}: KpiDistributionProps): ReactNode {
  const segments = items.filter((item) => item.inBar && item.value > 0);
  const summary = items.map((item) => `${item.value} ${item.label}`).join(', ');
  return (
    <section className="kpi-dist" aria-label={label}>
      <p className="kpi-dist-total">
        <span className="kpi-dist-value">{total}</span>
        <span className="kpi-dist-label">{totalLabel}</span>
      </p>
      <div className="kpi-dist-bar" role="img" aria-label={summary}>
        {total > 0
          ? segments.map((item) => (
              <span
                key={item.key}
                className={`tone-${item.tone}`}
                style={{ flexGrow: item.value }}
                title={`${item.value} ${item.label}`}
              />
            ))
          : null}
      </div>
      <ul className={onSelect ? 'kpi-dist-legend interactive' : 'kpi-dist-legend'}>
        {items.map((item) => {
          const content = <><strong>{item.value}</strong> {item.label}</>;
          return (
            <li key={item.key}>
              {onSelect ? (
                <button
                  type="button"
                  className={`kpi-dist-item tone-${item.tone}${selectedKey === item.key ? ' selected' : ''}`}
                  title={item.title}
                  aria-pressed={selectedKey === item.key}
                  aria-controls={controls}
                  aria-label={`${item.label}: ${item.value} anzeigen`}
                  onClick={() => onSelect(item.key)}
                >
                  {content}
                </button>
              ) : (
                <span className={`kpi-dist-item tone-${item.tone}`} title={item.title}>{content}</span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default KpiDistribution;
